import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { DateInputFR } from '../../DateInputFR';
import { NIVEAUX, niveau, PROBABILITES, IMPACTS } from './niveaux';

const VIDE = { risque: '', categorie: 'pedagogie', probabilite: 2, impact: 2, mesures: '', responsable: '', date_revue: '', action_id: '' };
const select = 'h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm';
const Champ = ({ label, children }) => <label className="block space-y-1 text-sm"><span className="font-medium text-slate-700">{label}</span>{children}</label>;

export const corpsRisque = (r) => Object.fromEntries(Object.keys(VIDE).map((k) => [k, r[k] ?? VIDE[k]]));

export const RisqueDialog = ({ risque, categories, onClose, onSaved }) => {
  const [f, setF] = useState(VIDE);
  const [busy, setBusy] = useState(false);
  const [confirmer, setConfirmer] = useState(false);
  useEffect(() => { setF({ ...VIDE, ...(risque || {}) }); setConfirmer(false); }, [risque]);
  const set = (k, num) => (v) => setF((x) => ({ ...x, [k]: num ? Number(v.target.value) : v?.target ? v.target.value : v }));
  const existant = !!risque?.risque_id;
  const n = NIVEAUX[niveau(f.probabilite, f.impact)];
  const enregistrer = async () => {
    setBusy(true);
    try {
      if (existant) await axios.put(`/qualiopi/risques/${risque.risque_id}`, corpsRisque(f)); else await axios.post('/qualiopi/risques', corpsRisque(f));
      toast.success(existant ? 'Risque mis à jour' : 'Risque ajouté à la cartographie');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'enregistrement"); } finally { setBusy(false); }
  };
  const supprimer = async () => {
    try { await axios.delete(`/qualiopi/risques/${risque.risque_id}`); toast.success('Risque supprimé'); onSaved(); } catch (e) { toast.error(e.response?.data?.detail || 'Suppression impossible'); }
  };
  return (
    <Dialog open={!!risque} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="risque-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{existant ? 'Modifier le risque' : 'Nouveau risque'}</DialogTitle>
          <DialogDescription>Probabilité × impact = criticité. Les mesures préventives sont la preuve attendue pour l'indicateur 32.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2"><Champ label="Risque *"><Input value={f.risque} onChange={set('risque')} placeholder="Ex : panne d'un jet" data-testid="risque-libelle" /></Champ></div>
          <Champ label="Catégorie"><select value={f.categorie} onChange={set('categorie')} className={select} data-testid="risque-categorie">{Object.entries(categories).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Champ>
          <div className="flex items-end"><span className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${n.badge}`} data-testid="risque-criticite">Criticité {f.probabilite * f.impact} · {n.label}</span></div>
          <Champ label="Probabilité"><select value={f.probabilite} onChange={set('probabilite', true)} className={select} data-testid="risque-probabilite">{PROBABILITES.map((l, i) => <option key={l} value={i + 1}>{i + 1} – {l}</option>)}</select></Champ>
          <Champ label="Impact sur la formation"><select value={f.impact} onChange={set('impact', true)} className={select} data-testid="risque-impact">{IMPACTS.map((l, i) => <option key={l} value={i + 1}>{i + 1} – {l}</option>)}</select></Champ>
          <div className="sm:col-span-2"><Champ label="Mesures préventives"><Textarea rows={3} value={f.mesures} onChange={set('mesures')} data-testid="risque-mesures" /></Champ></div>
          <Champ label="Responsable"><Input value={f.responsable} onChange={set('responsable')} data-testid="risque-responsable" /></Champ>
          <Champ label="Revu le"><DateInputFR value={f.date_revue} onChange={set('date_revue')} data-testid="risque-date-revue" /></Champ>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          {existant ? (confirmer
            ? <span className="flex gap-1"><Button variant="destructive" size="sm" onClick={supprimer} data-testid="risque-supprimer-confirm">Confirmer la suppression</Button><Button variant="ghost" size="sm" onClick={() => setConfirmer(false)}>Annuler</Button></span>
            : <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setConfirmer(true)} data-testid="risque-supprimer">Supprimer</Button>) : <span />}
          <Button onClick={enregistrer} disabled={busy || !f.risque.trim()} className="bg-[#0F172A] text-white hover:bg-[#1e293b]" data-testid="risque-enregistrer">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
